import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star, TrendingUp, MessageSquare, Award, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function StarBar({ value }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} className={`h-3.5 w-3.5 ${n <= Math.round(value) ? "text-amber-500 fill-amber-500" : "text-slate-200"}`} />
      ))}
      <span className="text-xs text-slate-600 ml-2 tabular-nums">{value.toFixed(2)}</span>
    </div>
  );
}

export default function EvaluationsAdminPage() {
  const [ranking, setRanking] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/erp/evaluations/global");
      setRanking(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (t) => {
    setSelected(t);
    try {
      const { data } = await api.get(`/erp/evaluations/summary/${t.teacher_id}`);
      setDetail(data);
    } catch { setDetail({ count: 0, averages: {}, comments: [] }); }
  };

  return (
    <div className="space-y-6" data-testid="eval-admin-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Award className="h-3 w-3" /><span>QUALITÉ PÉDAGOGIQUE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Évaluations enseignants</h1>
          <p className="text-slate-500 mt-1">Classement anonyme basé sur les évaluations des élèves. Visible uniquement par le Fondateur et la Direction.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">
            <TrendingUp className="h-4 w-4 inline mr-2" />Classement général
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Enseignant</th>
                  <th className="px-3 py-2 text-left">Note</th>
                  <th className="px-3 py-2 text-right">Évals</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((r, i) => (
                  <tr key={r.teacher_id}
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selected?.teacher_id === r.teacher_id ? "bg-blue-50" : ""}`}
                      onClick={() => openDetail(r)}
                      data-testid={`rank-row-${r.teacher_id}`}>
                    <td className="px-3 py-2 font-bold text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{r.teacher_name}</td>
                    <td className="px-3 py-2"><StarBar value={r.overall} /></td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                  </tr>
                ))}
                {ranking.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-12 text-center text-slate-400">Aucune évaluation reçue pour le moment.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">
            Détail {selected ? `— ${selected.teacher_name}` : ""}
          </div>
          {!selected && <div className="p-6 text-center text-slate-400 text-sm">Sélectionnez un enseignant pour voir le détail</div>}
          {selected && detail && (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {["quality", "pedagogy", "punctuality", "mastery", "behavior"].map(k => (
                  <div key={k} className="flex items-center justify-between border border-slate-100 rounded-lg p-2">
                    <span className="text-slate-600 capitalize">{{
                      quality: "Qualité", pedagogy: "Pédagogie", punctuality: "Ponctualité",
                      mastery: "Maîtrise", behavior: "Comportement",
                    }[k]}</span>
                    <StarBar value={detail.averages?.[k] || 0} />
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="text-xs uppercase font-bold text-slate-500 mb-2 flex items-center gap-2"><MessageSquare className="h-3 w-3" />Commentaires anonymes ({detail.comments?.length || 0})</div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {(detail.comments || []).map((c, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm">
                      <div className="text-xs text-slate-500 mb-1">Note : {c.average}/5 — {new Date(c.created_at).toLocaleDateString("fr-FR")}</div>
                      <div className="text-slate-700">{c.comment}</div>
                    </div>
                  ))}
                  {(!detail.comments || detail.comments.length === 0) && (
                    <div className="text-center text-slate-400 text-sm py-4">Aucun commentaire.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
